import React, { useEffect, useState } from 'react';
import {
  Popover,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { MedicationEvent, EditOptions } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import ScheduleEventDetails from './ScheduleEventDetails';
import ScheduleEventHistory from './ScheduleEventHistory';
import ScheduleEventImages from './ScheduleEventImages';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../../store';
import { MED_EVENT_IMAGE_ROLES } from '../../../../../../shared/constants';
import { getCurrentUserRole } from '../../../../../../shared/utils';
import {
  darkPurple,
  dimGrey,
  inactive,
  lightBlack,
  lightTeal,
} from '../../../../../../mainTheme';

const tabStyles = {
  indicator: {
    backgroundColor: lightTeal,
  },
  tab: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    color: dimGrey,
    lineHeight: '20px',
    letterSpacing: '0.24px',
  },
  selected: {
    color: `${lightBlack} !important`,
  },
};

interface ScheduleEventPopupProps {
  event: MedicationEvent | null;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  switchToMedicationTab?: () => void;
  customerId: number;
}

const ScheduleEventPopup: React.FC<ScheduleEventPopupProps> = ({
  event,
  anchorEl,
  open,
  onClose,
  onEdit,
  switchToMedicationTab,
  customerId,
}) => {
  const [editMenuAnchor, setEditMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  // 0: Details, 1: History, 2: Medication images (only when a dispense captured images)
  const [tab, setTab] = useState(0);

  const dashboardUserInfo = useSelector(
    (state: RootState) => state.dashboardUser?.dashboardUserInfo,
  );
  const canViewImages = MED_EVENT_IMAGE_ROLES.includes(
    getCurrentUserRole(dashboardUserInfo),
  );

  const showImagesTab = canViewImages && event?.has_dispense_images === true;

  const editMenuOpen = Boolean(editMenuAnchor);

  // Reset tab to Details mode when modal closes
  useEffect(() => {
    if (!open) setTab(0);
  }, [open]);

  // Images tab can vanish when the popup is re-anchored to another event
  useEffect(() => {
    if (tab === 2 && !showImagesTab) setTab(0);
  }, [tab, showImagesTab]);

  if (!event) return null;

  // Open edit options menu
  const handleEditClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setEditMenuAnchor(event.currentTarget);
  };

  // Close edit options menu
  const handleEditMenuClose = () => {
    setEditMenuAnchor(null);
  };

  // Handle edit option selection
  const handleEditOptionSelect = (_optionType: EditOptions['type']) => {
    handleEditMenuClose();
    onEdit();
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            width: 640,
            borderRadius: '16px',
            border: `1px solid ${inactive}`,
            background: '#FFF',
            boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.10)',
            p: 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            padding: { xs: '16px 24px', sm: '20px 32px' },
            flexDirection: 'column',
            alignItems: 'flex-start',
            minHeight: 320,
            height: 454,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '16px', sm: '18px' },
                fontWeight: 700,
                lineHeight: '22px',
                letterSpacing: '0.2px',
                color: darkPurple,
              }}
            >
              {event.title}
            </Typography>
            <Box
              display="flex"
              justifyContent="end"
              borderRadius="8px"
              border="1px solid var(--Outline-O5---Separator, #D4D4D9)"
            >
              <Tooltip title="Edit">
                <IconButton onClick={handleEditClick} size="small">
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    style={{ marginLeft: '4px', marginRight: '4px' }}
                    size="sm"
                  />
                </IconButton>
              </Tooltip>

              {/* For now commenting out the delete option */}
              {/* <Box
                width="1px"
                bgcolor="var(--Outline-O5---Separator, #D4D4D9)"
                marginY={1}
              /> */}
              {/* <Tooltip title="Delete">
                <IconButton size="small">
                  <FontAwesomeIcon
                    icon={faTrashCan}
                    size="sm"
                    style={{ marginLeft: '4px', marginRight: '4px' }}
                  />
                </IconButton>
              </Tooltip> */}
            </Box>
          </Box>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': tabStyles.indicator,
              '& .MuiTab-root': tabStyles.tab,
              '& .Mui-selected': tabStyles.selected,
            }}
          >
            <Tab label="Details" />
            <Tab label="History" />
            {showImagesTab && <Tab label="Medication images" />}
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ width: '100%' }}>
            {tab === 0 && (
              <ScheduleEventDetails
                event={event}
                switchToMedicationTab={switchToMedicationTab}
              />
            )}
            {tab === 1 && (
              <ScheduleEventHistory customerId={customerId} event={event} />
            )}
            {tab === 2 && showImagesTab && (
              <ScheduleEventImages customerId={customerId} event={event} />
            )}
          </Box>
        </Box>
      </Popover>

      {/* Edit Options Menu */}
      <Menu
        anchorEl={editMenuAnchor}
        open={editMenuOpen}
        onClose={handleEditMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            borderRadius: '8px',
            boxShadow:
              '0px 1px 4px 0px rgba(0, 0, 0, 0.06), 0px 6px 14px 0px rgba(0, 0, 0, 0.08), 0px 4px 10px 0px rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <MenuItem
          onClick={() => handleEditOptionSelect('this_event')}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            padding: '8px 16px',
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '20px',
              color: lightBlack,
              fontWeight: 500,
            }}
          >
            This event
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handleEditOptionSelect('this_and_following')}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            padding: '8px 16px',
          }}
          disabled
        >
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '20px',
              color: lightBlack,
              fontWeight: 500,
            }}
          >
            This and all following events
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handleEditOptionSelect('all_events')}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            padding: '8px 16px',
          }}
          disabled
        >
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '20px',
              color: lightBlack,
              fontWeight: 500,
            }}
          >
            All events in the series
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ScheduleEventPopup;
